import React, { useCallback, useEffect, useState } from 'react';
import { Box, Divider, Typography, IconButton, Chip, Menu, MenuItem, useMediaQuery, Button, Pagination } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NewsCommentList from '../../components/News/NewsCommentList';
import NewsCommentForm from '../../components/News/NewsCommentForm';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getNewsDetailApi,
  getNewsCommentsApi,
  createNewsCommentApi,
  deleteNewsCommentApi,
  updateNewsCommentApi,
  getNewsRecommendStatusApi,
  addNewsRecommendApi,
  boostNewsRecommendApi,
  removeNewsRecommendApi,
  deleteNewsApi,
  likeNewsCommentApi,
  unlikeNewsCommentApi,
  boostNewsCommentLikeApi,
} from '../../api';

const formatDate = (createdAt) => {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const getAuthorDisplayName = (news) => {
  if (!news) return '';
  return (
    news.author_name ||
    news.authorName ||
    news.writer_name ||
    news.writerName ||
    news.user_name ||
    news.userName ||
    news.author?.name ||
    ''
  );
};

const COMMENT_PAGE_SIZE = 10;

const NewsDetail = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [news, setNews] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentPage, setCommentPage] = useState(1);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentLoading, setCommentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isRecommended, setIsRecommended] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [recommendLoginModalOpen, setRecommendLoginModalOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width:768px)');

  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleNavigateAdjacent = (targetId) => {
    if (!targetId) return;
    navigate(`/news/${targetId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToList = () => {
    const fromList = location.state?.from;
    if (fromList) {
      navigate(fromList);
      return;
    }
    navigate('/news');
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/news/write/${id}`, { state: { news } });
  };

  const handleDeleteRequest = () => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteNewsApi(id);
      navigate('/news');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete news:', e);
      if (e?.status === 401 || e?.suppressAlert) return;
      window.alert(e.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        setCurrentUser(JSON.parse(raw));
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setError(null);
        const res = await getNewsDetailApi(id);
        const data = res?.data || res;
        setNews(data || null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load news detail:', e);
        setError(e.message || '게시글을 불러오지 못했습니다.');
      }
    };

    if (!id) return;

    loadDetail();
  }, [id]);

  const fetchComments = useCallback(async (pageToLoad = commentPage) => {
    if (!id) return;

    try {
      setCommentLoading(true);
      const res = await getNewsCommentsApi(id, { page: pageToLoad, limit: COMMENT_PAGE_SIZE });
      const result = res?.data || res || {};
      const items = Array.isArray(result.items)
        ? result.items
        : Array.isArray(result)
          ? result
          : [];
      const total = Number(result.total ?? 0);
      const totalPages = Math.max(1, Math.ceil(total / COMMENT_PAGE_SIZE));

      if (pageToLoad > totalPages) {
        setCommentPage(totalPages);
        return;
      }

      setComments(items || []);
      setCommentTotal(total);
      setCommentPage(Number(result.page) || pageToLoad);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load comments:', e);
      setComments([]);
    } finally {
      setCommentLoading(false);
    }
  }, [commentPage, id]);

  useEffect(() => {
    setCommentPage(1);
    setCommentTotal(0);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchComments(commentPage);
  }, [fetchComments, commentPage, id]);

  useEffect(() => {
    const loadRecommendStatus = async () => {
      if (!currentUser || !id) return;
      try {
        const res = await getNewsRecommendStatusApi(id);
        const data = res?.data || res;
        setIsRecommended(Boolean(data?.recommended));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load recommend status:', e);
        setIsRecommended(false);
      }
    };

    loadRecommendStatus();
  }, [currentUser, id]);

  const reloadComments = async (pageToLoad = commentPage) => {
    await fetchComments(pageToLoad);
  };

  const handleSubmitComment = async (text, { parentCommentId = null, replyToCommentId = null, displayName = null } = {}) => {
    if (!currentUser) {
      window.alert('로그인 후 댓글을 남겨주세요.');
      return;
    }

    try {
      setCommentSubmitting(true);
      const payload = {
        news: id,
        parentCommentId,
        replyToCommentId,
        content: text,
      };
      if (displayName) {
        payload.displayName = displayName;
      }
      await createNewsCommentApi(payload);
      const nextPage = parentCommentId
        ? commentPage
        : Math.max(Math.ceil((commentTotal + 1) / COMMENT_PAGE_SIZE), 1);
      await reloadComments(nextPage);
      setNews((prev) =>
        prev
          ? {
              ...prev,
              comment_count: (prev.comment_count || 0) + 1,
            }
          : prev,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create comment:', e);
      if (e?.status === 401 || e?.suppressAlert) return;
      window.alert(e.message || '댓글 등록 중 오류가 발생했습니다.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    try {
      await deleteNewsCommentApi(comment.comment_id);
      await reloadComments();
      setNews((prev) =>
        prev
          ? {
              ...prev,
              comment_count: Math.max((prev.comment_count || 0) - 1, 0),
            }
          : prev,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete comment:', e);
      if (e?.status === 401 || e?.suppressAlert) return;
      window.alert(e.message || '댓글 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleUpdateComment = async (commentId, content, displayName = null) => {
    try {
      await updateNewsCommentApi(commentId, content, displayName);
      await reloadComments();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to update comment:', e);
      if (e?.status === 401 || e?.suppressAlert) return;
      window.alert(e.message || '댓글 수정 중 오류가 발생했습니다.');
    }
  };

  const handleRequestDeleteComment = (comment) => {
    setCommentToDelete(comment);
  };

  const handleConfirmDeleteComment = async () => {
    if (!commentToDelete) return;
    await handleDeleteComment(commentToDelete);
    setCommentToDelete(null);
  };

  const handleToggleCommentLike = async (comment, willLike) => {
    if (!currentUser) {
      window.alert('로그인 후 이용 가능합니다.');
      return;
    }

    try {
      const isAdminUser = currentUser?.role === 'ADMIN';
      if (isAdminUser) {
        const res = await boostNewsCommentLikeApi(comment.comment_id);
        const data = res?.data || res || {};
        const nextCount =
          typeof data.likeCount === 'number'
            ? data.likeCount
            : (comment.like_count || 0) + 1;
        setComments((prev) =>
          prev.map((item) =>
            item.comment_id === comment.comment_id
              ? {
                  ...item,
                  like_count: nextCount,
                  liked_by_viewer: 1,
                }
              : item,
          ),
        );
        return;
      }

      const api = willLike ? likeNewsCommentApi : unlikeNewsCommentApi;
      const res = await api(comment.comment_id);
      const data = res?.data || res || {};
      const nextCount =
        typeof data.likeCount === 'number'
          ? data.likeCount
          : Math.max((comment.like_count || 0) + (willLike ? 1 : -1), 0);
      setComments((prev) =>
        prev.map((item) =>
          item.comment_id === comment.comment_id
            ? {
                ...item,
                like_count: nextCount,
                liked_by_viewer: willLike ? 1 : 0,
              }
            : item,
        ),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle comment like:', e);
      if (e?.status === 401 || e?.suppressAlert) return;
      window.alert(e.message || '댓글 좋아요 처리 중 오류가 발생했습니다.');
    }
  };

  const handleToggleRecommend = () => {
    if (!currentUser) {
      setRecommendLoginModalOpen(true);
      return;
    }

    if (currentUser?.role === 'ADMIN') {
      setIsRecommended(true);

      const doBoost = async () => {
        try {
          await boostNewsRecommendApi(id);
          setNews((prevNews) =>
            prevNews
              ? {
                  ...prevNews,
                  recommend_count: (prevNews.recommend_count || 0) + 1,
                }
              : prevNews,
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to boost recommend:', e);
          if (e?.status === 401 || e?.suppressAlert) return;
          window.alert(e.message || '추천 처리 중 오류가 발생했습니다.');
        }
      };

      doBoost();
      return;
    }

    setIsRecommended((prev) => !prev);

    const doRequest = async () => {
      try {
        if (!isRecommended) {
          await addNewsRecommendApi(id);
          setNews((prevNews) =>
            prevNews
              ? {
                  ...prevNews,
                  recommend_count: (prevNews.recommend_count || 0) + 1,
                }
              : prevNews,
          );
        } else {
          await removeNewsRecommendApi(id);
          setNews((prevNews) =>
            prevNews
              ? {
                  ...prevNews,
                  recommend_count: Math.max((prevNews.recommend_count || 0) - 1, 0),
                }
              : prevNews,
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to toggle recommend:', e);
        if (e?.status === 401 || e?.suppressAlert) return;
        window.alert(e.message || '추천 처리 중 오류가 발생했습니다.');
        setIsRecommended((prev) => !prev);
      }
    };

    doRequest();
  };

  const handleShareClick = async () => {
    const url = window.location.href;
    const shareData = {
      title: news?.title || '마닐라 소식',
      url: url,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        if (e.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Share failed:', e);
        }
      }
      return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const tempInput = document.createElement('textarea');
        tempInput.value = url;
        tempInput.style.position = 'fixed';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      if (!isMobile) setShareCopied(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy link:', e);
      window.alert('링크를 복사하지 못했습니다.');
    }
  };

  useEffect(() => {
    if (!shareCopied) return;
    const timer = setTimeout(() => setShareCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [shareCopied]);

  const currentUserId = currentUser?.id || currentUser?.user_id || currentUser?.userId;
  const authorId = news?.author_id || news?.authorId;
  const isWriter = currentUserId && authorId && String(currentUserId) === String(authorId);
  const isAdmin = currentUser?.role === 'ADMIN';
  const canShowMenu = isWriter || isAdmin;
  const previousNewsId = news?.previous_news?.news_id;
  const nextNewsId = news?.next_news?.news_id;
  const commentTotalPages = Math.max(1, Math.ceil(commentTotal / COMMENT_PAGE_SIZE));
  const commentPageList = Array.from({ length: commentTotalPages }, (_, idx) => idx + 1);

  return (
    <Box
      sx={{
        maxWidth: 430,
        mx: 'auto',
        bgcolor: 'white',
        minHeight: '100vh',
        borderRadius: 0,
        boxShadow: '0 0 20px rgba(0,0,0,0.06)',
        px: 2,
        pb: 2,
        position: 'relative',
      }}
    >
      <MainMenuDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onNavigate={handleNavigate}
      />

      <Header
        variant="full"
        onClickMenu={() => setMenuOpen(true)}
      />

      <Box
        sx={{
          mb: 2,
          mt: 1,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          width: 'fit-content',
        }}
        onClick={handleBackToList}
      >
        <ArrowBackIosIcon sx={{ fontSize: 18, mr: 0.5 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 16 }}>
          마닐라 소식
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        {news && !error && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: 12 }}
              >
                {(() => {
                  const author = getAuthorDisplayName(news);
                  const date = formatDate(news.created_at);
                  return (
                    <>
                      {author ? (
                        <Box component="span" sx={{ color: '#111',fontSize: 13, fontWeight: 600 }}>
                          {author}
                        </Box>
                      ) : null}
                      {author && date ? <Box component="span"> · </Box> : null}
                      {date ? <Box component="span">{date}</Box> : null}
                    </>
                  );
                })()}
              </Typography>

              {canShowMenu && (
                <>
                  <IconButton
                    size="small"
                    onClick={handleMenuClick}
                    sx={{ p: 0.5, mr: -0.5 }}
                  >
                    <MoreVertIcon sx={{ fontSize: 20, color: '#999' }} />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        mt: 0.5,
                        minWidth: 100,
                      },
                    }}
                  >
                    {isWriter && (
                      <MenuItem onClick={handleEdit} sx={{ fontSize: 14 }}>
                        수정
                      </MenuItem>
                    )}
                    {(isWriter || isAdmin) && (
                      <MenuItem onClick={handleDeleteRequest} sx={{ fontSize: 14, color: '#d32f2f' }}>
                        삭제
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip
                label={news.header_name || '기타'}
                size="small"
                color="default"
                sx={{
                  borderRadius: 1,
                  bgcolor: news.header_color || '#F0EFED',
                  mr: 1,
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 16 }}>
                {news.title}
              </Typography>
            </Box>

            <Typography
              variant="body1"
              component="div"
              sx={{
                mt: 1,
                fontSize: 14,
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
                '& video': {
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'contain',
                  margin: '8px 0 12px',
                },
              }}
              dangerouslySetInnerHTML={{ __html: news.content || '' }}
            />
          </>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          color: 'text.secondary',
          fontSize: 12,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            onClick={handleToggleRecommend}
          >
            {isRecommended ? (
              <ThumbUpAltRoundedIcon sx={{ fontSize: 20, color: '#3679FF' }} />
            ) : (
              <ThumbUpAltOutlinedIcon sx={{ fontSize: 20 }} />
            )}{' '}
            {news?.recommend_count ?? 0}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              component="img"
              src="/icon/chat_icon(2).png"
              alt="댓글 아이콘"
              sx={{ width: 16, height: 16 }}
            />
            {news?.comment_count ?? 0}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />{' '}
            {news?.view_count ?? 0}
          </Box>
        </Box>
        <IconButton size="small" onClick={handleShareClick}>
          <ShareOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 15 }}>
            댓글
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.4 }}>
            <Button
              size="small"
              variant="outlined"
              sx={{
                fontSize: 12.5,
                color: previousNewsId ? '#111827' : '#999',
                px: 1,
                minWidth: 'auto',
                borderColor: '#e5e7eb',
                bgcolor: '#f7f7f8',
                borderRadius: 1.5,
                '&:hover': { bgcolor: '#f0f2f5', borderColor: '#dfe3e8' },
              }}
              disabled={!previousNewsId}
              onClick={() => handleNavigateAdjacent(previousNewsId)}
            >
              이전
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={{
                fontSize: 12.5,
                color: nextNewsId ? '#111827' : '#999',
                px: 1,
                minWidth: 'auto',
                borderColor: '#e5e7eb',
                bgcolor: '#f7f7f8',
                borderRadius: 1.5,
                '&:hover': { bgcolor: '#f0f2f5', borderColor: '#dfe3e8' },
              }}
              disabled={!nextNewsId}
              onClick={() => handleNavigateAdjacent(nextNewsId)}
            >
              다음
            </Button>
          </Box>
        </Box>
        <NewsCommentList
          comments={comments}
          currentUser={currentUser}
          submitting={commentSubmitting}
          onSubmitReply={({ parentCommentId, replyToCommentId, text, displayName }) =>
            handleSubmitComment(text, { parentCommentId, replyToCommentId, displayName })
          }
          onUpdate={handleUpdateComment}
          onDelete={handleRequestDeleteComment}
          onToggleLike={handleToggleCommentLike}
        />
        {commentLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            댓글을 불러오는 중입니다...
          </Typography>
        )}
        {commentTotalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1.5,
              pointerEvents: commentLoading ? 'none' : 'auto',
              opacity: commentLoading ? 0.6 : 1,
            }}
          >
            <Pagination
              count={commentTotalPages}
              page={commentPage}
              onChange={(_event, value) => setCommentPage(value)}
              size="small"
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>
        )}
        <NewsCommentForm
          currentUser={currentUser}
          submitting={commentSubmitting}
          onSubmit={(text, displayName) => handleSubmitComment(text, { parentCommentId: null, replyToCommentId: null, displayName })}
        />
      </Box>

      {shareCopied && !isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            px: 2.5,
            py: 1.2,
            borderRadius: 3,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1500,
            backdropFilter: 'blur(4px)',
            animation: 'fadeIn 0.3s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translate(-50%, 10px)' },
              to: { opacity: 1, transform: 'translate(-50%, 0)' },
            },
          }}
        >
          링크가 공유되었습니다.
        </Box>
      )}

      <ConfirmModal
        open={Boolean(commentToDelete)}
        message="댓글을 삭제하시겠습니까?"
        onConfirm={handleConfirmDeleteComment}
      />

      <ConfirmModal
        open={recommendLoginModalOpen}
        message="로그인 후 추천할 수 있습니다."
        onConfirm={() => setRecommendLoginModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        message="게시글을 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </Box>
  );
};

export default NewsDetail;


